import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Download, Calendar, Save, FileArchive, FileText, Table } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Organization {
  id: string;
  name: string;
}

interface ExportTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function ExportTab({ organization, onSave }: ExportTabProps) {
  const { toast } = useToast()
  
  const [oneTimeExport, setOneTimeExport] = useState({
    format: 'zip',
    source: 'all',
    include_documents: true,
    include_reports: true,
    include_comments: true,
    include_metadata: true,
    date_range: 'all'
  })

  const [scheduledExport, setScheduledExport] = useState({
    enabled: false,
    frequency: 'weekly',
    format: 'zip',
    source: 'completed_cases',
    day: 'sunday',
    time: '02:00',
    recipients: ['admin@example.com']
  })

  const handleSave = () => {
    onSave({ oneTimeExport, scheduledExport })
  }

  const startExport = async () => {
    toast({
      title: "Export påbörjad",
      description: "Din export bearbetas och kommer att skickas via e-post när den är klar.",
    })
    
    // Simulate export process
    setTimeout(() => {
      toast({
        title: "Export klar",
        description: "Exporten har skickats till din e-postadress.",
      })
    }, 3000)
  }

  const exportFormats = [
    { value: 'zip', label: 'ZIP-arkiv', icon: FileArchive },
    { value: 'pdf', label: 'PDF-rapport', icon: FileText },
    { value: 'csv', label: 'CSV-data', icon: Table }
  ]

  const sourceOptions = [
    { value: 'all', label: 'Alla ärenden' },
    { value: 'completed_cases', label: 'Slutförda ärenden' },
    { value: 'specific_case', label: 'Specifikt ärende' },
    { value: 'by_module', label: 'Per modul' }
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Engångsexport
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Exportera data direkt för nedladdning eller e-post
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Format</Label>
              <Select value={oneTimeExport.format} onValueChange={(value) => setOneTimeExport(prev => ({ ...prev, format: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {exportFormats.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      <div className="flex items-center gap-2">
                        <format.icon className="h-4 w-4" />
                        {format.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Källa</Label>
              <Select value={oneTimeExport.source} onValueChange={(value) => setOneTimeExport(prev => ({ ...prev, source: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sourceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-base font-medium">Inkludera i export</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[
                { key: 'include_documents', label: 'Dokument' },
                { key: 'include_reports', label: 'Rapporter' },
                { key: 'include_comments', label: 'Kommentarer' },
                { key: 'include_metadata', label: 'Metadata' }
              ].map((item) => (
                <div key={item.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={item.key}
                    checked={oneTimeExport[item.key as keyof typeof oneTimeExport] as boolean}
                    onCheckedChange={(checked) => 
                      setOneTimeExport(prev => ({ ...prev, [item.key]: checked }))
                    }
                  />
                  <Label htmlFor={item.key} className="text-sm">
                    {item.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Datumintervall</Label>
            <Select value={oneTimeExport.date_range} onValueChange={(value) => setOneTimeExport(prev => ({ ...prev, date_range: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla datum</SelectItem>
                <SelectItem value="last_30_days">Senaste 30 dagarna</SelectItem>
                <SelectItem value="last_90_days">Senaste 90 dagarna</SelectItem>
                <SelectItem value="this_year">Detta år</SelectItem>
                <SelectItem value="custom">Anpassat intervall</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={startExport} className="gap-2 w-full">
            <Download className="h-4 w-4" />
            Starta export nu
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Schemalagd export
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Automatisk export enligt schema
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Aktivera schemalagd export</Label>
              <p className="text-sm text-muted-foreground">Automatisk export skickas via e-post</p>
            </div>
            <Switch
              checked={scheduledExport.enabled}
              onCheckedChange={(checked) => setScheduledExport(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {scheduledExport.enabled && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frekvens</Label>
                  <Select value={scheduledExport.frequency} onValueChange={(value) => setScheduledExport(prev => ({ ...prev, frequency: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Dagligen</SelectItem>
                      <SelectItem value="weekly">Veckovis</SelectItem>
                      <SelectItem value="monthly">Månadsvis</SelectItem>
                      <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Format</Label>
                  <Select value={scheduledExport.format} onValueChange={(value) => setScheduledExport(prev => ({ ...prev, format: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {exportFormats.map((format) => (
                        <SelectItem key={format.value} value={format.value}>
                          <div className="flex items-center gap-2">
                            <format.icon className="h-4 w-4" />
                            {format.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {scheduledExport.frequency === 'weekly' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Dag</Label>
                    <Select value={scheduledExport.day} onValueChange={(value) => setScheduledExport(prev => ({ ...prev, day: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sunday">Söndag</SelectItem>
                        <SelectItem value="monday">Måndag</SelectItem>
                        <SelectItem value="tuesday">Tisdag</SelectItem>
                        <SelectItem value="wednesday">Onsdag</SelectItem>
                        <SelectItem value="thursday">Torsdag</SelectItem>
                        <SelectItem value="friday">Fredag</SelectItem>
                        <SelectItem value="saturday">Lördag</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Tid</Label>
                    <Input
                      type="time"
                      value={scheduledExport.time}
                      onChange={(e) => setScheduledExport(prev => ({ ...prev, time: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>Källa</Label>
                <Select value={scheduledExport.source} onValueChange={(value) => setScheduledExport(prev => ({ ...prev, source: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>E-postmottagare</Label>
                <Input
                  value={scheduledExport.recipients.join(', ')}
                  onChange={(e) => setScheduledExport(prev => ({ ...prev, recipients: e.target.value.split(', ') }))}
                  placeholder="admin@example.com, user@example.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Separera flera e-postadresser med kommatecken
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Exporthistorik</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="text-center py-8 text-muted-foreground">
              <p>Ingen exporthistorik tillgänglig än</p>
              <p className="text-sm">Historik kommer att visas när du gör din första export</p>
            </div>
            {[].map((export_item, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                <div>
                  <p className="text-sm font-medium">{export_item.type} - {export_item.format}</p>
                  <p className="text-xs text-muted-foreground">{export_item.date}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{export_item.status}</p>
                  <p className="text-xs text-muted-foreground">{export_item.size}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Save className="h-4 w-4" />
          Spara exportinställningar
        </Button>
      </div>
    </div>
  )
}